// Flows as files: read one out of the panel, put one back in.
//
// A flow is a JSON document, so a file is the natural way to move it between environments
// (test panel → prod), to keep a version of it outside the database before a risky edit,
// and to hand a working transcription to someone who has to start from an empty canvas.
// The registry imports a file as a NEW flow; the editor exports the one it has open and
// can replace its document from a file.
//
// The shape written and read here is the registry row plus its document:
//   { task_type, app, name, note, flow: { app, package?, version?, steps: [...] } }
// A file holding only the inner document (a bare `{ app, steps }`) is accepted too — the
// missing record fields simply have no opinion, and the caller supplies them.
import { t } from '../ui/lang';
import { TASK_TYPES } from './flows-shared';
/** A flat list of step nodes, each an object carrying a string `op` — same test the server runs. */
function isNodeArray(v) {
    return Array.isArray(v) && v.every((n) => n != null && typeof n === 'object' && !Array.isArray(n) && typeof n.op === 'string');
}
/**
 * Read a parsed file into a FlowFile, or throw with what is wrong with it.
 *
 * Deliberately shallow: this only checks that the thing is a flow document at all. The
 * node vocabulary is validated by the editor (flowProblems) and, authoritatively, by the
 * clicker's engine before it runs anything — a file with an op this panel has never heard
 * of should still open, so it can be looked at.
 */
export function parseFlowFile(raw) {
    if (raw == null || typeof raw !== 'object' || Array.isArray(raw))
        throw new Error(t('pf.err_json_shape'));
    const o = raw;
    // A bare document (no record fields around it) is its own flow.
    const docRaw = (o.flow ?? (Array.isArray(o.steps) ? o : null));
    if (!docRaw || typeof docRaw !== 'object' || Array.isArray(docRaw))
        throw new Error(t('pf.err_json_no_flow'));
    if (!isNodeArray(docRaw.steps))
        throw new Error(t('pf.err_json_steps'));
    const app = String(docRaw.app ?? o.app ?? '').trim();
    if (!app)
        throw new Error(t('pf.err_json_app'));
    const doc = { ...docRaw, app };
    const taskType = String(o.task_type ?? '').trim();
    return {
        ...(TASK_TYPES.includes(taskType) ? { task_type: taskType } : {}),
        app,
        ...(typeof o.name === 'string' && o.name.trim() ? { name: o.name.trim() } : {}),
        ...(typeof o.note === 'string' ? { note: o.note } : {}),
        flow: doc,
    };
}
/** Open the OS file picker and return the flow it holds; null when the user cancels. */
export function pickFlowFile() {
    return new Promise((resolve, reject) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'application/json,.json';
        input.style.display = 'none';
        document.body.appendChild(input);
        // 'change' never fires on cancel in any browser, so the picker is also cleaned up
        // when the window comes back focused with nothing chosen.
        const done = (v) => { input.remove(); resolve(v); };
        input.addEventListener('cancel', () => done(null));
        input.addEventListener('change', () => {
            const file = input.files?.[0];
            if (!file) {
                done(null);
                return;
            }
            file.text()
                .then((text) => { done(parseFlowFile(JSON.parse(text))); })
                .catch((e) => { input.remove(); reject(e); });
        });
        input.click();
    });
}
/** File name for an exported flow: the flow's own name, safe for a filesystem. */
export function flowFileName(name) {
    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60);
    return `${slug || 'flow'}.flow.json`;
}
/** Hand the browser a flow to save. Pretty-printed — these files get read and diffed. */
export function downloadFlowFile(payload) {
    const blob = new Blob([`${JSON.stringify(payload, null, 2)}\n`], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = flowFileName(payload.name ?? payload.app);
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoke on the next tick — Safari aborts the download if the URL dies synchronously.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}
